import {
    data_from_server_post,
    get_base_url,
    post_formdata_with_progress,
    put_blob_with_progress,
} from '@/shared/http/client';
import {
    TaskType,
    DatasetOrigin,
    isGenerativeTask,
    getLabelColumnPluralLabel,
} from '@/shared/domain/taskTypes';
import {
    showNetworkError,
    showUploadError,
    showValidationError,
    validateFile,
} from '@/features/datasets/utils/uploadUtils';
import {
    initTwoPhaseProgressItem,
    updateTwoPhaseUploadProgress,
    markTwoPhaseUploadCompleteStartProcessing,
    markTwoPhaseFailed,
} from '@/features/datasets/utils/uploadProgress';
import { detectFileFormat, isJsonLikeFormat } from '@/features/datasets/utils/filePreviewUtils';
import { extractParquetBase64 } from '@/features/datasets/utils/parquetMetadata';
import { isEmptyGenerateWithAi } from '@/features/datasets/utils/emptyResponsesHandling';

/**
 * Shared dataset-upload building blocks used by both the legacy
 * `UploadDataTab` upload modal and the creation-wizard Data Collection step.
 * Everything here is component-free: callers pass their own state setters,
 * GlobalContext functions and success callbacks explicitly.
 */

// ---------------------------------------------------------------------------
// Template download links (per task type)
// ---------------------------------------------------------------------------

export const CLASSIFICATION_SINGLE_LABEL_TEMPLATE_LINKS = [
    { text: 'Download JSON', href: '/classification-single-label-example.json' },
    { text: 'Download JSONL', href: '/classification-single-label-example.jsonl' },
    { text: 'Download CSV', href: '/classification-single-label-example.csv' },
];

export const CLASSIFICATION_MULTI_LABEL_TEMPLATE_LINKS = [
    { text: 'Download JSON', href: '/classification-example.json' },
    { text: 'Download JSONL', href: '/classification-example.jsonl' },
    { text: 'Download CSV', href: '/classification-example.csv' },
];

export const TEMPLATE_LINKS_BY_TASK_TYPE = {
    [TaskType.NER]: [
        { text: 'Download JSON', href: '/ner-example.json' },
        { text: 'Download JSONL', href: '/ner-example.jsonl' },
    ],
    // Default classification links are multi-label; prefer
    // `templateLinksForTaskType(taskType, { isMultilabel })` so single-label
    // tasks get the single-label templates.
    [TaskType.classification]: CLASSIFICATION_MULTI_LABEL_TEMPLATE_LINKS,
    [TaskType.summarization]: [
        { text: 'Download JSON', href: '/summarization-example.json' },
        { text: 'Download JSONL', href: '/summarization-example.jsonl' },
        { text: 'Download CSV', href: '/summarization-example.csv' },
    ],
    [TaskType.contentGeneration]: [
        { text: 'Download JSON', href: '/content-generation-example.json' },
        { text: 'Download JSONL', href: '/content-generation-example.jsonl' },
        { text: 'Download CSV', href: '/content-generation-example.csv' },
    ],
};

/**
 * Agentic template datasets (`frontend/public/agentic/`, generated by
 * `tools/agentic_templates/build_agentic_templates.py`): flat `example` +
 * task response column + `messages` trajectory. No CSV — trajectories are
 * nested arrays, so agentic tasks accept json/jsonl/parquet only.
 */
const agenticTemplateLinks = (basename) => [
    { text: 'Download JSON', href: `/agentic/${basename}-agentic-example.json` },
    { text: 'Download JSONL', href: `/agentic/${basename}-agentic-example.jsonl` },
    { text: 'Download Parquet', href: `/agentic/${basename}-agentic-example.parquet` },
];

export const AGENTIC_TEMPLATE_LINKS_BY_TASK_TYPE = {
    [TaskType.NER]: agenticTemplateLinks('ner'),
    [TaskType.NER_WITH_RELATIONS]: agenticTemplateLinks('ner'),
    [TaskType.classification]: agenticTemplateLinks('classification'),
    [TaskType.summarization]: agenticTemplateLinks('summarization'),
    [TaskType.contentGeneration]: agenticTemplateLinks('content-generation'),
};

export const AGENTIC_CLASSIFICATION_SINGLE_LABEL_TEMPLATE_LINKS = agenticTemplateLinks(
    'classification-single-label'
);

/**
 * Example-dataset download links for the given task type.
 * @param {string} taskType
 * @param {{ isMultilabel?: boolean, isAgentic?: boolean }} [options]  For
 *   classification, pass `isMultilabel: false` to get single-label templates
 *   (one label per row). Defaults to multi-label templates when omitted /
 *   true. `isAgentic` switches to the trajectory (`messages`) templates.
 */
export function templateLinksForTaskType(taskType, { isMultilabel, isAgentic = false } = {}) {
    if (isAgentic) {
        if (taskType === TaskType.classification && isMultilabel === false) {
            return AGENTIC_CLASSIFICATION_SINGLE_LABEL_TEMPLATE_LINKS;
        }
        return (
            AGENTIC_TEMPLATE_LINKS_BY_TASK_TYPE[taskType] ||
            AGENTIC_TEMPLATE_LINKS_BY_TASK_TYPE[TaskType.NER]
        );
    }
    if (taskType === TaskType.classification) {
        return isMultilabel === false
            ? CLASSIFICATION_SINGLE_LABEL_TEMPLATE_LINKS
            : CLASSIFICATION_MULTI_LABEL_TEMPLATE_LINKS;
    }
    return TEMPLATE_LINKS_BY_TASK_TYPE[taskType] || TEMPLATE_LINKS_BY_TASK_TYPE[TaskType.NER];
}

// ---------------------------------------------------------------------------
// Allowed formats / accept attribute
// ---------------------------------------------------------------------------

export const ALLOWED_FORMATS_BY_TASK_TYPE = {
    [TaskType.classification]: new Set(['csv', 'json', 'jsonl', 'parquet']),
    [TaskType.NER]: new Set(['json', 'jsonl', 'parquet']),
    [TaskType.NER_WITH_RELATIONS]: new Set(['json', 'jsonl', 'parquet']),
    [TaskType.summarization]: new Set(['csv', 'json', 'jsonl', 'parquet']),
    [TaskType.contentGeneration]: new Set(['csv', 'json', 'jsonl', 'parquet']),
};

/** Agentic tasks carry nested `messages` trajectories, which CSV cannot hold. */
export const AGENTIC_ALLOWED_FORMATS = new Set(['json', 'jsonl', 'parquet']);

const supportsCsv = (taskType) =>
    taskType === TaskType.classification || isGenerativeTask(taskType);

/**
 * @param {string} taskType
 * @param {{ isAgentic?: boolean }} [options]
 */
export function allowedFormatsForTaskType(taskType, { isAgentic = false } = {}) {
    if (isAgentic) return AGENTIC_ALLOWED_FORMATS;
    return ALLOWED_FORMATS_BY_TASK_TYPE[taskType] || new Set(['json', 'jsonl', 'parquet']);
}

/**
 * @param {string} taskType
 * @param {{ isAgentic?: boolean }} [options]
 */
export function acceptAttributeForTaskType(taskType, { isAgentic = false } = {}) {
    return !isAgentic && supportsCsv(taskType)
        ? '.csv,.json,.jsonl,.parquet'
        : '.json,.jsonl,.parquet';
}

/**
 * User-facing supported-formats label (NER and agentic tasks have no CSV
 * support; agentic tasks call out parquet since the templates ship as one).
 * @param {string} taskType
 * @param {{ isAgentic?: boolean }} [options]
 */
export function supportedFormatsLabelForTaskType(taskType, { isAgentic = false } = {}) {
    if (isAgentic) return 'JSON/JSONL/Parquet format supported';
    return supportsCsv(taskType)
        ? 'CSV/JSON/JSONL format supported'
        : 'JSON/JSONL format supported';
}

// ---------------------------------------------------------------------------
// File content reading (base64, chunked for large files)
// ---------------------------------------------------------------------------

/**
 * Maximum file size to read into memory (10MB) — enough for column
 * extraction, label sampling, and preview. For larger files only a sample is
 * read to avoid browser crashes.
 */
export const MAX_FILE_CHUNK_SIZE = 10 * 1024 * 1024;

export async function readDatasetFileContent(file, options = {}) {
    const { chunkOnly = false } = options;
    const format = detectFileFormat(file);
    const isBinary = format === 'parquet';

    if (isBinary && chunkOnly && file.size > MAX_FILE_CHUNK_SIZE) {
        return extractParquetBase64(file);
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;

        if (isBinary) {
            reader.onload = (e) => {
                const bytes = new Uint8Array(e.target.result);
                let binary = '';
                for (let i = 0; i < bytes.length; i++) {
                    binary += String.fromCharCode(bytes[i]);
                }
                resolve(btoa(binary));
            };
            reader.readAsArrayBuffer(file);
        } else {
            reader.onload = (e) => {
                const content = e.target.result;
                resolve(btoa(unescape(encodeURIComponent(content))));
            };
            if (chunkOnly && file.size > MAX_FILE_CHUNK_SIZE) {
                reader.readAsText(file.slice(0, MAX_FILE_CHUNK_SIZE));
            } else {
                reader.readAsText(file);
            }
        }
    });
}

// ---------------------------------------------------------------------------
// Column extraction (`get-file-columns`)
// ---------------------------------------------------------------------------

/**
 * Extract only the portion of a base64 file needed for column extraction, to
 * significantly reduce the payload size for large files.
 */
export function extractFileContentForColumns(fullContent, fileType) {
    if (!fullContent) return null;

    try {
        if (fileType === 'parquet') {
            return fullContent;
        }

        if (isJsonLikeFormat(fileType) || fileType === 'application/json') {
            // Ensures we can extract all columns like "example", "entities", "relations", etc.
            const maxChunkSize = (2 * 1024 * 1024 * 4) / 3; // 2MB decoded = ~2.67MB base64
            const chunkSize = Math.min(maxChunkSize, fullContent.length);
            return fullContent.substring(0, chunkSize);
        }

        // For CSV files, decode a small chunk first to check structure.
        const estimatedBytesNeeded = fileType === 'csv' ? 1024 : 64 * 1024;
        const base64ChunkSize = Math.ceil((estimatedBytesNeeded * 4) / 3);

        const initialChunk = fullContent.substring(0, Math.min(base64ChunkSize, fullContent.length));
        let decodedChunk;
        try {
            decodedChunk = decodeURIComponent(escape(atob(initialChunk)));
        } catch (e) {
            const paddedChunk = initialChunk + '='.repeat((4 - (initialChunk.length % 4)) % 4);
            decodedChunk = decodeURIComponent(escape(atob(paddedChunk)));
        }

        if (fileType === 'csv') {
            // For CSV, only the header row is needed.
            const firstNewline = decodedChunk.indexOf('\n');
            const firstCr = decodedChunk.indexOf('\r');
            const lineEnd =
                firstNewline === -1
                    ? firstCr === -1
                        ? decodedChunk.length
                        : firstCr
                    : firstCr === -1
                      ? firstNewline
                      : Math.min(firstNewline, firstCr);

            if (lineEnd < decodedChunk.length) {
                const headerLine = decodedChunk.substring(0, lineEnd);
                return btoa(unescape(encodeURIComponent(headerLine)));
            }
        }

        // The backend can handle partial content and will request more if needed.
        return initialChunk;
    } catch (error) {
        console.error('Error extracting file content for columns:', error);
        const safeChunkSize = Math.min(8192, fullContent.length);
        return fullContent.substring(0, safeChunkSize);
    }
}

/**
 * Fetch column names (and the first rows) for an uploaded (in-browser) file.
 * Resolves to `{ columns: string[], sampleRows: object[] }` — `sampleRows`
 * are plain records (nested values intact) so the wizard can preview fields
 * the browser cannot parse itself (parquet, agentic `messages` trajectories).
 */
export function fetchFileColumnsWithSamples({ filename, clientId, fileContent, fileType }) {
    return new Promise((resolve, reject) => {
        const fileContentForColumns = extractFileContentForColumns(fileContent, fileType);
        data_from_server_post(
            'get-file-columns',
            {
                filename,
                client_id: clientId,
                file_content: fileContentForColumns,
                file_type: fileType,
            },
            (response) => {
                if (response?.success && response.columns) {
                    resolve({
                        columns: response.columns,
                        sampleRows: Array.isArray(response.sample_rows)
                            ? response.sample_rows.filter(
                                  (row) => row && typeof row === 'object' && !Array.isArray(row)
                              )
                            : [],
                    });
                } else {
                    reject(new Error(response?.error || 'Failed to fetch file columns'));
                }
            },
            (error) => reject(new Error(error || 'Failed to fetch file columns'))
        );
    });
}

/** Fetch column names for an uploaded (in-browser) file. Resolves to string[]. */
export function fetchFileColumns(params) {
    return fetchFileColumnsWithSamples(params).then(({ columns }) => columns);
}

// ---------------------------------------------------------------------------
// Agentic trajectories (`get-trajectory-role-values`)
// ---------------------------------------------------------------------------

/**
 * Scan the whole picked file for the distinct role values found under
 * `<trajectoryColumn>[*].<roleKey>` (plus the union of message-object keys).
 * Resolves the raw endpoint response
 * (`{ success, role_values, message_keys, total_role_values, error }`).
 * Files over the 20MB preview cap are rejected by the backend with an error.
 */
export async function fetchTrajectoryRoleValues({ file, fileType, trajectoryColumn, roleKey }) {
    if (!file || !fileType || !trajectoryColumn) return null;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);
    formData.append('trajectory_column', trajectoryColumn);
    formData.append('role_key', roleKey || 'role');

    const response = await fetch(
        `${get_base_url(window.location.origin)}/get-trajectory-role-values`,
        {
            method: 'POST',
            body: formData,
            credentials: 'include',
        }
    );
    if (!response.ok) {
        let errorDetail = '';
        try {
            const errorData = await response.json();
            errorDetail = errorData?.error || errorData?.detail || '';
        } catch {
            errorDetail = await response.text();
        }
        throw new Error(errorDetail || 'Failed to read trajectory roles');
    }
    return response.json();
}

// ---------------------------------------------------------------------------
// Label extraction (`get-file-labels-upload`)
// ---------------------------------------------------------------------------

export const MAX_PREVIEW_FILE_SIZE_BYTES = 20 * 1024 * 1024; // 20MB
export const PREVIEW_TRUNCATION_BYTES = MAX_PREVIEW_FILE_SIZE_BYTES;

/**
 * User-facing notice shown when a picked file exceeds the preview size limit:
 * only the first 20MB is parsed for the extracted labels/entities preview,
 * while the full file is still uploaded and processed.
 */
export function previewTruncationMessage(taskType) {
    return `File is too large to preview all extracted ${getLabelColumnPluralLabel(taskType)}. The preview below shows data from the first 20MB only. The full file will still be uploaded and processed.`;
}

/**
 * Upload (a truncated preview of) the picked file to extract the distinct
 * labels found in `labelColumn`. Resolves the raw endpoint response
 * (`{ success, labels, label_examples, error }`).
 */
export async function fetchFileLabelsWithUpload({
    file,
    filename,
    clientId,
    labelColumn,
    fileType,
    taskType,
    delimiter = null,
}) {
    if (!file || !filename || !clientId || !fileType || !labelColumn) return null;

    const isTruncatedPreview = file.size > MAX_PREVIEW_FILE_SIZE_BYTES;
    const previewBlob = isTruncatedPreview ? file.slice(0, PREVIEW_TRUNCATION_BYTES) : file;
    const previewFile = isTruncatedPreview
        ? new File([previewBlob], file.name || filename, { type: file.type })
        : file;

    const formData = new FormData();
    // Send only the first N bytes when preview is truncated.
    formData.append('file', previewFile);
    formData.append('filename', filename);
    formData.append('client_id', clientId);
    formData.append('label_column', labelColumn);
    formData.append('file_type', fileType);
    formData.append('task_type', taskType);
    if (isTruncatedPreview) {
        formData.append('preview_truncated', 'true');
        formData.append('original_size_bytes', String(file.size));
        formData.append('preview_size_bytes', String(PREVIEW_TRUNCATION_BYTES));
    }
    if (delimiter !== undefined && delimiter !== null) {
        formData.append('delimiter', delimiter);
    }

    const response = await fetch(`${get_base_url(window.location.origin)}/get-file-labels-upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
    });

    if (!response.ok) {
        let errorDetail = '';
        try {
            const errorData = await response.json();
            errorDetail = errorData?.error || errorData?.detail || '';
        } catch {
            errorDetail = await response.text();
        }
        throw new Error(errorDetail || 'Failed to fetch labels');
    }

    return response.json();
}

/**
 * Extract distinct labels from already-read file bytes (`get-file-labels`).
 * Used after Next→Back when the File handle has been dropped but `content`
 * is still in the slot cache.
 */
export function fetchFileLabelsFromContent({
    filename,
    clientId,
    labelColumn,
    fileContent,
    fileType,
    taskType,
    delimiter = null,
}) {
    if (!filename || !clientId || !labelColumn || !fileContent || !fileType) {
        return Promise.resolve(null);
    }
    return new Promise((resolve, reject) => {
        data_from_server_post(
            'get-file-labels',
            {
                filename,
                client_id: clientId,
                label_column: labelColumn,
                file_content: fileContent,
                file_type: fileType,
                task_type: taskType,
                delimiter,
            },
            (response) => {
                if (response?.success && Array.isArray(response.labels)) {
                    resolve(response);
                    return;
                }
                resolve({ success: false, labels: response?.labels || [] });
            },
            (error) => reject(new Error(error || 'Failed to fetch labels'))
        );
    });
}

/**
 * Copy extra_params[labelColumn] onto the task response (labels / entities /
 * summary / output) of an already-ingested dataset — wizard Next after the
 * user re-picked the mapped column. `datasetName` narrows the update to one
 * clone (per-file details mode); omitted means every clone of the target.
 */
export function applyLabelColumnToDataset({
    clientId,
    usecaseId,
    isBenchmark = false,
    labelColumnName,
    labelMappings = {},
    delimiter = null,
    datasetName = null,
}) {
    return new Promise((resolve, reject) => {
        data_from_server_post(
            'apply-label-column-to-dataset',
            {
                client_id: clientId,
                usecase_id: usecaseId,
                is_benchmark: isBenchmark,
                label_column_name: labelColumnName,
                label_mappings: labelMappings,
                classification_labels_delimiter: delimiter,
                dataset_name: datasetName || null,
            },
            (response) => {
                if (response?.success) {
                    resolve(response);
                    return;
                }
                reject(new Error(response?.detail || response?.error || 'Failed to apply labels'));
            },
            (error) => reject(new Error(error || 'Failed to apply labels'))
        );
    });
}

/**
 * Re-point the example of an already-ingested prompt-mapped dataset at a
 * different dynamic field — wizard Next after the user changed the Primary
 * model input in the Map prompt template modal. The extracted values already
 * live in extra_params per record, so no re-upload happens; the backend
 * no-ops when the stored primary is unchanged. `datasetName` narrows the
 * update to one clone (per-file details mode).
 */
export function applyExampleFieldToDataset({
    clientId,
    usecaseId,
    isBenchmark = false,
    exampleFieldName,
    datasetName = null,
}) {
    return new Promise((resolve, reject) => {
        data_from_server_post(
            'apply-example-field-to-dataset',
            {
                client_id: clientId,
                usecase_id: usecaseId,
                is_benchmark: isBenchmark,
                example_field_name: exampleFieldName,
                dataset_name: datasetName || null,
            },
            (response) => {
                if (response?.success) {
                    resolve(response);
                    return;
                }
                reject(
                    new Error(
                        response?.detail ||
                            response?.error ||
                            'Failed to apply the primary model input'
                    )
                );
            },
            (error) => reject(new Error(error || 'Failed to apply the primary model input'))
        );
    });
}

/**
 * Empty response.labels on an already-ingested clone after the wizard
 * chooses "Add without labels" on a later Next.
 */
export function clearLabelColumnFromDataset({
    clientId,
    usecaseId,
    isBenchmark = false,
}) {
    return new Promise((resolve, reject) => {
        data_from_server_post(
            'clear-label-column-from-dataset',
            {
                client_id: clientId,
                usecase_id: usecaseId,
                is_benchmark: isBenchmark,
            },
            (response) => {
                if (response?.success) {
                    resolve(response);
                    return;
                }
                reject(new Error(response?.detail || response?.error || 'Failed to clear labels'));
            },
            (error) => reject(new Error(error || 'Failed to clear labels'))
        );
    });
}

// ---------------------------------------------------------------------------
// Two-phase upload orchestration
// (create-dataset-upload-url → signed PUT → complete-dataset-upload)
// ---------------------------------------------------------------------------

/**
 * `complete-dataset-upload` / clone form fields for an agentic trajectory
 * mapping. Nothing is appended when there is no trajectory column — the
 * backend treats an empty `trajectory_column_name` as "no trajectories".
 *
 * @param {FormData} formData
 * @param {{ columnName?: string, roleKey?: string, contentKey?: string,
 *           toolCallKey?: string|null, toolCallNameKey?: string|null,
 *           toolCallArgumentsKey?: string|null,
 *           roleMapping?: Record<string, string> }|null} mapping
 */
export function appendTrajectoryMappingFields(formData, mapping) {
    const columnName = (mapping?.columnName || '').trim();
    if (!columnName) return;
    formData.append('trajectory_column_name', columnName);
    formData.append('trajectory_role_key', (mapping.roleKey || 'role').trim() || 'role');
    formData.append(
        'trajectory_content_key',
        (mapping.contentKey || 'content').trim() || 'content'
    );
    // An empty tool-call key tells the backend the file has no tool calls.
    const toolCallKey = (mapping.toolCallKey || '').trim();
    formData.append('trajectory_tool_call_key', toolCallKey);
    // Keys inside each tool-call object (Map Tool Call Keys); empty = the
    // backend's `name` / `arguments` defaults.
    formData.append(
        'trajectory_tool_call_name_key',
        toolCallKey ? (mapping.toolCallNameKey || '').trim() : ''
    );
    formData.append(
        'trajectory_tool_call_arguments_key',
        toolCallKey ? (mapping.toolCallArgumentsKey || '').trim() : ''
    );
    formData.append('trajectory_role_mapping', JSON.stringify(mapping.roleMapping || {}));
}

/**
 * Full two-phase dataset/benchmark upload flow, extracted from
 * `UploadDataTab` so the creation wizard can drive the same backend flow.
 *
 * @param {object} p
 * @param {File}   p.file
 * @param {string} p.type                'dataset' | 'benchmark' | 'external'
 * @param {object} p.currentUsecase
 * @param {string} p.selectedTaskType
 * @param {string|null} p.renamedFileName  Dataset name override (no extension).
 * @param {string|null} p.delimiter        Multilabel CSV label delimiter.
 * @param {object} p.context   GlobalContext slice: setProgressStatus,
 *                             updateUsecase, registerUploadAbort,
 *                             unregisterUploadAbort, registerUploadGroup,
 *                             clearCancelledUploadSuppression, currentSubGroup.
 * @param {object} p.callbacks onDatasetSuccess(name), onBenchmarkSuccess(name),
 *                             onUploadSuccess(), refreshLibraryOptions(),
 *                             setIsDataAnalysisLoading(bool),
 *                             onLabelMappingsApplied(labelMappings, relationsMappings).
 */
export async function uploadDatasetFile({
    file,
    type,
    currentUsecase,
    selectedTaskType,
    renamedFileName = null,
    delimiter = null,
    datasetOrigin = null,
    labelMappings = {},
    exampleColumnName = 'example',
    labelColumnName = 'labels',
    relationsColumnName = '',
    relationsMappings = {},
    examplesAreWithPrompt = false,
    promptTemplate = '',
    dynamicFieldsInPromptTemplate = [],
    exampleDynamicField = null,
    originLabelsColumnName = '',
    subtaskMappings = null,
    rejectionLabelName = null,
    emptyMultilabelHandling = 'none_selected',
    /** Generative: empty response column + true → stamp specific_ai_labeled. */
    generateResponsesWithAi = false,
    deferLabelGeneration = false,
    /** Original file columns so unlabeled uploads still persist the labels column. */
    fileColumnNames = null,
    /**
     * Agentic trajectories ("Does your data contain trajectories? = Yes"):
     * `{ columnName, roleKey, contentKey, toolCallKey, toolCallNameKey,
     * toolCallArgumentsKey, roleMapping }`. Null or an empty `columnName`
     * means the file has no trajectories.
     */
    trajectoryMapping = null,
    context = {},
    callbacks = {},
}) {
    const {
        setProgressStatus,
        updateUsecase,
        registerUploadAbort,
        unregisterUploadAbort,
        registerUploadGroup,
        clearCancelledUploadSuppression,
        currentSubGroup,
    } = context;
    const {
        onDatasetSuccess,
        onBenchmarkSuccess,
        onUploadSuccess,
        refreshLibraryOptions,
        setIsDataAnalysisLoading,
        onLabelMappingsApplied,
    } = callbacks;

    // Validate using the effective filename (renamed if provided)
    const effectiveFileName = renamedFileName
        ? `${renamedFileName}.${file.name.split('.').pop()}`
        : file.name;
    const validation = validateFile(
        { name: effectiveFileName, type: file.type },
        { selectedTaskType, isBenchmark: type === 'benchmark' }
    );

    if (!validation.isValid) {
        showValidationError(validation.error);
        return;
    }

    const formData = new FormData();

    // Create a new file with the renamed filename if available
    let fileToUpload = file;
    if (renamedFileName) {
        const fileExtension = file.name.split('.').pop();
        const newFileName = `${renamedFileName}.${fileExtension}`;
        fileToUpload = new File([file], newFileName, { type: file.type });
    }

    formData.append('client_id', currentUsecase.client_id);
    formData.append('usecase_id', currentUsecase._id);

    // Required parameters for complete-dataset-upload (same as legacy upload-dataset, without file)
    formData.append('is_benchmark', type === 'benchmark');
    formData.append('task_type', selectedTaskType);

    // AI labeling: empty response column + (class/NER catalog OR generative
    // generateResponsesWithAi), or empty-only generate_with_ai while keeping
    // the response column. Unlabeled keeps the user-selected origin and
    // does not force specific_ai_labeled / LABEL_ONLY.
    const emptyResponseColumn =
        labelColumnName === '' || labelColumnName == null;
    const hasAiCatalog =
        Boolean(labelMappings) && Object.keys(labelMappings).length > 0;
    const isGenerativeTaskType =
        selectedTaskType === TaskType.summarization ||
        selectedTaskType === TaskType.contentGeneration;
    const isEmptyOnlyAi = isEmptyGenerateWithAi(emptyMultilabelHandling);
    const isAiLabelingUpload =
        isEmptyOnlyAi ||
        (emptyResponseColumn && hasAiCatalog) ||
        (isGenerativeTaskType && emptyResponseColumn && generateResponsesWithAi);

    const effectiveDatasetOrigin = isAiLabelingUpload
        ? DatasetOrigin.specific_ai_labeled
        : datasetOrigin;

    if (effectiveDatasetOrigin && effectiveDatasetOrigin !== 'skip') {
        formData.append('data_creation_type', effectiveDatasetOrigin);
    }
    // Creation wizard: defer LABEL_ONLY until Model Configuration Next.
    if (deferLabelGeneration && isAiLabelingUpload) {
        formData.append('defer_label_generation', 'true');
    }

    formData.append('label_mappings', JSON.stringify(labelMappings));
    formData.append('example_column_name', exampleColumnName);
    formData.append('label_column_name', labelColumnName);
    // Single-label classification confidence-threshold fallback: which label
    // (post-mapping) acts as the "Other" catch-all. Backend ignores it for
    // non-single-label task types.
    if (rejectionLabelName) {
        formData.append('rejection_label_name', rejectionLabelName);
    }
    // Classification only — "Upload without labels": snapshot the file's original label column
    // into an `origin_labels` extra column instead of using it as labels.
    if (originLabelsColumnName) {
        formData.append('origin_labels_column_name', originLabelsColumnName);
    }
    if (relationsColumnName) {
        formData.append('relations_column_name', relationsColumnName);
    }
    if (Object.keys(relationsMappings).length > 0) {
        formData.append('relations_mappings', JSON.stringify(relationsMappings));
    }
    if (Array.isArray(fileColumnNames) && fileColumnNames.length) {
        formData.append('file_column_names', JSON.stringify(fileColumnNames));
    }

    // Multi-objective uploads: forward the full per-objective mappings
    // and a client-generated `upload_group_id`. The backend fans the one
    // HTTP upload out into N atomic per-subtask Kafka events so each
    // subtask is processed in its own per-usecase clone collection. The
    // `upload_group_id` is echoed back via WebSocket progress so the
    // FloatingProgressBar can coalesce N progress events into a single
    // card (see `globalState.jsx#registerUploadGroup`).
    const isMultiObjective = Array.isArray(subtaskMappings) && subtaskMappings.length > 1;
    if (isMultiObjective) {
        formData.append('subtask_mappings', JSON.stringify(subtaskMappings));
        const uploadGroupId =
            typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : `g_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        formData.append('upload_group_id', uploadGroupId);
    }

    // Prompt template parameters (example_column_name above contains the prompt+example).
    // FastAPI parses the string 'true'/'false' as boolean.
    formData.append('examples_are_with_prompt', examplesAreWithPrompt ? 'true' : 'false');
    if (examplesAreWithPrompt) {
        if (promptTemplate) {
            formData.append('prompt_template', promptTemplate);
        }
        if (dynamicFieldsInPromptTemplate && dynamicFieldsInPromptTemplate.length > 0) {
            dynamicFieldsInPromptTemplate.forEach((field) => {
                formData.append('dynamic_fields_in_prompt_template', field);
            });
        }
        if (exampleDynamicField) {
            formData.append('example_dynamic_field_name', exampleDynamicField);
        }
    }

    appendTrajectoryMappingFields(formData, trajectoryMapping);

    const datasetName = renamedFileName || file.name.split('.')[0];

    // When uploading through a task, the backend creates both raw + clone.
    // Predict the clone name so optimistic UI updates match the backend.
    // Multi-objective uploads create N clones (one per subtask), so the
    // user-visible progress entry is keyed by the raw `datasetName`
    // instead of any one child's clone name. Each child clone is
    // registered under that group key so per-child WS progress events
    // coalesce into a single card.
    const isManualPredictions = type === 'external';
    const expectedCloneName =
        currentUsecase?._id && !isManualPredictions
            ? `${datasetName.slice(0, 40)}__t_${currentUsecase._id.slice(-6)}`
            : null;
    const optimisticName = isMultiObjective ? datasetName : expectedCloneName || datasetName;

    // Build the per-subtask clone-name list for both upload-group routing
    // and optimistic UI updates on each sibling usecase below.
    const childCloneNames = isMultiObjective
        ? subtaskMappings
              .map((m) => {
                  const id = m && m.llm_usecase_id;
                  if (!id) return null;
                  return `${datasetName.slice(0, 40)}__t_${id.slice(-6)}`;
              })
              .filter(Boolean)
        : [];
    if (isMultiObjective && childCloneNames.length > 0) {
        registerUploadGroup?.(optimisticName, childCloneNames, {
            datasetDisplayName: optimisticName,
            affectedUsecaseIds: subtaskMappings.map((m) => m?.llm_usecase_id).filter(Boolean),
            clientId: currentUsecase.client_id,
            isBenchmark: type === 'benchmark',
            usecaseIdPrimary: currentUsecase._id,
        });
    }

    // Optimistically update the usecase to include the new dataset/benchmark
    // so the loading state only shows for uploads to this usecase.
    // Multi-objective uploads attach a distinct clone name per sibling
    // usecase so each per-usecase Data Editor lights up immediately.
    const currentDatasets = Array.isArray(currentUsecase?.datasets)
        ? [...currentUsecase.datasets]
        : [];
    const currentBenchmarks = Array.isArray(currentUsecase?.benchmarks)
        ? [...currentUsecase.benchmarks]
        : [];
    const primaryAttachmentName = isMultiObjective
        ? expectedCloneName || datasetName
        : optimisticName;
    const existedOnUsecaseBeforeUpload =
        type === 'benchmark'
            ? currentBenchmarks.includes(primaryAttachmentName)
            : currentDatasets.includes(primaryAttachmentName);

    if (type === 'benchmark') {
        if (!currentBenchmarks.includes(primaryAttachmentName)) {
            updateUsecase(currentUsecase._id, {
                benchmarks: [...currentBenchmarks, primaryAttachmentName],
            });
        }
    } else if (!currentDatasets.includes(primaryAttachmentName)) {
        updateUsecase(currentUsecase._id, {
            datasets: [...currentDatasets, primaryAttachmentName],
        });
    }
    // Optimistically reflect the (re-pointed or cleared) single-label
    // classification "Other"/rejection label so EditTaskModal and other
    // views show the new value immediately. The backend persists the
    // authoritative value on upload completion.
    if (
        type !== 'benchmark' &&
        !isMultiObjective &&
        selectedTaskType === TaskType.classification &&
        currentUsecase?.is_multilabel !== true
    ) {
        const newRejectionLabelName = rejectionLabelName || null;
        if ((currentUsecase?.rejection_label_name || null) !== newRejectionLabelName) {
            updateUsecase(currentUsecase._id, {
                rejection_label_name: newRejectionLabelName,
            });
        }
    }

    if (isMultiObjective) {
        const siblingUsecases = currentSubGroup?.usecases || [];
        for (const sibling of siblingUsecases) {
            if (!sibling?._id || sibling._id === currentUsecase?._id) continue;
            const siblingCloneName = `${datasetName.slice(0, 40)}__t_${sibling._id.slice(-6)}`;
            if (type === 'benchmark') {
                const siblingBenchmarks = Array.isArray(sibling.benchmarks)
                    ? [...sibling.benchmarks]
                    : [];
                if (!siblingBenchmarks.includes(siblingCloneName)) {
                    updateUsecase(sibling._id, {
                        benchmarks: [...siblingBenchmarks, siblingCloneName],
                    });
                }
            } else {
                const siblingDatasets = Array.isArray(sibling.datasets) ? [...sibling.datasets] : [];
                if (!siblingDatasets.includes(siblingCloneName)) {
                    updateUsecase(sibling._id, {
                        datasets: [...siblingDatasets, siblingCloneName],
                    });
                }
            }
        }
    }

    clearCancelledUploadSuppression?.(optimisticName);

    initTwoPhaseProgressItem({
        setProgressStatus,
        key: optimisticName,
        datasetName: optimisticName,
        usecaseId: currentUsecase._id,
        message: 'Uploading file',
        existedOnUsecaseBeforeUpload,
        isLabelGeneration: isAiLabelingUpload,
        deferLabelGeneration: Boolean(deferLabelGeneration && isAiLabelingUpload),
        isBenchmark: type === 'benchmark',
        affectedUsecaseIds: isMultiObjective
            ? subtaskMappings.map((m) => m?.llm_usecase_id).filter(Boolean)
            : undefined,
    });

    try {
        // Pass delimiter for CSV multilabel classification. Always send a
        // delimiter for multilabel: without it the data processor treats the
        // task as single-label and stores empty rows as "Unlabeled" ([])
        // instead of honoring the "Empty responses" choice. Fall back to the
        // multilabel default ("list") the modal uses when the state is unset.
        const isCsv =
            file.type === 'text/csv' || (file.name && file.name.toLowerCase().endsWith('.csv'));
        const shouldSendDelimiter =
            selectedTaskType === TaskType.classification && isCsv && currentUsecase?.is_multilabel;
        if (shouldSendDelimiter) {
            formData.append('classification_labels_delimiter', delimiter || 'list');
        }

        // How to handle empty response rows (labels / entities / summary /
        // output). Multilabel adds 'none_selected' ([""]); other task types use
        // 'unlabeled' ([]) or 'exclude' (skip). Only the single-objective path
        // uses this; multi-objective carries it per-subtask. AI imports keep
        // unlabeled in data_processor when data_creation_type=specific_ai_labeled.
        if (!isMultiObjective) {
            formData.append('empty_multilabel_handling', emptyMultilabelHandling);
        }

        const base = get_base_url(window.location.origin);
        const createFd = new FormData();
        createFd.append('client_id', currentUsecase.client_id);
        createFd.append('filename', fileToUpload.name);
        createFd.append('content_type', fileToUpload.type || 'application/octet-stream');
        // Link the upload to its task from the very first moment so auto-train
        // ("Train automatically when data is ready") already counts it as an
        // active flow while the browser is still PUTting the file bytes.
        if (currentUsecase._id) {
            createFd.append('usecase_id', currentUsecase._id);
        }
        createFd.append('is_benchmark', type === 'benchmark');

        const created = await post_formdata_with_progress(`${base}/create-dataset-upload-url`, createFd, {
            withCredentials: true,
        });
        if (created.aborted) {
            return;
        }
        if (!created.ok) {
            let errorDetail = '';
            try {
                const errorData = created?.data;
                errorDetail =
                    typeof errorData === 'string'
                        ? errorData
                        : errorData?.detail || errorData?.message || created?.rawText || '';
            } catch {
                errorDetail = 'Unknown error occurred';
            }
            showUploadError(file.name, errorDetail);
            markTwoPhaseFailed({
                setProgressStatus,
                key: optimisticName,
                errorMessage: errorDetail,
                details: errorDetail,
            });
            return;
        }

        const putResp = await put_blob_with_progress(
            created.data.upload_url,
            fileToUpload,
            created.data.content_type,
            {
                onUploadProgress: ({ percent }) => {
                    if (percent == null) return;
                    updateTwoPhaseUploadProgress({
                        setProgressStatus,
                        key: optimisticName,
                        uploadPercent: percent,
                    });
                },
                onAbortReady: (abortFn) => {
                    registerUploadAbort(optimisticName, abortFn);
                },
            }
        );
        if (putResp.aborted) {
            return;
        }
        if (!putResp.ok) {
            const errorDetail = putResp.rawText || `Signed upload failed (${putResp.status})`;
            showUploadError(file.name, errorDetail);
            markTwoPhaseFailed({
                setProgressStatus,
                key: optimisticName,
                errorMessage: errorDetail,
                details: errorDetail,
            });
            return;
        }

        formData.append('object_key', created.data.object_key);
        formData.append('status_id', created.data.status_id);
        formData.append('upload_filename', fileToUpload.name);
        formData.append('signed_content_type', created.data.content_type);

        const response = await post_formdata_with_progress(`${base}/complete-dataset-upload`, formData, {
            withCredentials: true,
            onUploadProgress: ({ percent }) => {
                if (percent == null) return;
                updateTwoPhaseUploadProgress({
                    setProgressStatus,
                    key: optimisticName,
                    uploadPercent: percent,
                });
            },
            onAbortReady: (abortFn) => {
                registerUploadAbort(optimisticName, abortFn);
            },
        });

        if (response.aborted) {
            return;
        }

        if (response.ok) {
            // Upload is complete; from here on, server-side processing progress advances the bar (50% -> 100%).
            markTwoPhaseUploadCompleteStartProcessing({ setProgressStatus, key: optimisticName });

            // Backend returns the effective dataset name (clone when uploading
            // through a task). Fall back to the optimistic name computed above.
            const successFileName = response.data?.dataset_name || optimisticName;

            // Ensure newly mapped labels are immediately available as replacement
            // options in subsequent uploads (e.g., benchmark upload after dataset upload).
            onLabelMappingsApplied?.(labelMappings, relationsMappings);

            if (type === 'benchmark' && onBenchmarkSuccess) {
                onBenchmarkSuccess(successFileName);
            } else if (type === 'dataset' && onDatasetSuccess) {
                onDatasetSuccess(successFileName);
            }
            if (onUploadSuccess) {
                onUploadSuccess();
            }
            refreshLibraryOptions?.();
            if (type === 'dataset') {
                setIsDataAnalysisLoading?.(true);
            }
        } else {
            let errorDetail = '';
            try {
                const errorData = response?.data;
                if (typeof errorData === 'string') {
                    errorDetail = errorData;
                } else {
                    errorDetail = errorData?.detail || errorData?.message || '';
                }
            } catch (parseError) {
                errorDetail = response?.rawText || 'Unknown error occurred';
            }
            showUploadError(file.name, errorDetail);
            markTwoPhaseFailed({
                setProgressStatus,
                key: optimisticName,
                errorMessage: errorDetail,
                details: errorDetail,
            });
        }
    } catch (error) {
        showNetworkError();
        markTwoPhaseFailed({
            setProgressStatus,
            key: optimisticName,
            errorMessage: error?.message || 'Network error occurred during upload.',
            details: error?.message,
        });
    } finally {
        unregisterUploadAbort(optimisticName);
        // The upload group mapping is intentionally kept until the user
        // dismisses the progress card (via `removeProgressItem`) or
        // cancels the upload, because per-child WebSocket progress events
        // can keep arriving for several seconds after the HTTP call
        // returns.
    }
}
